import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import {FormArray, FormBuilder, FormGroup} from '@angular/forms';
import {
  BokkzAlgDataService,
  BokkzJrDataService,
  BokkzJrLogic,
  EigenVermogenNatuurlijkPersoon,
  EigenVermogenNatuurlijkPersoonPriveOpnameStorting,
  JaarrapportageEntity,
  TrialBalanceItem,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  ReplaySubject,
  Subject,
  Subscription,
  takeUntil
} from 'rxjs';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule} from '@angular/common';
import {SpecTrialbalanceComponent} from '../spec-trialbalance/spec-trialbalance.component';
import {SpecFormattedexplanationComponent} from '../spec-formattedexplanation/spec-formattedexplanation.component';
import {SpecTrialbalanceReadOnlyComponent} from "../spec-trialbalance-read-only/spec-trialbalance-read-only.component";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-eigen-vermogen-natuurlijk-persoon',
  templateUrl: './spec-eigen-vermogen-natuurlijk-persoon.component.html',
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    SharedUiDialogModule,
    SharedUiButtonModule,
    CommonModule,
    SpecTrialbalanceComponent,
    SpecFormattedexplanationComponent,
    SpecTrialbalanceReadOnlyComponent,
  ],
  styleUrls: ['./spec-eigen-vermogen-natuurlijk-persoon.component.scss'],
})
export class SpecEigenVermogenNatuurlijkPersoonComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public id!: string;
  @Input() selectedTabObservable!: Observable<number>;
  private subscriptions = new Subscription();
  private isSaving = false;
  private destroy$ = new Subject<void>();
  private selectedTabSubject = new ReplaySubject<number>(1);
  showCustomControls = true;
  public selectedTab = 0;
  public index = 0;
  public dialogVisible = false;
  public jrEntity = {} as JaarrapportageEntity;
  public indexNatuurlijkPersoon = 0 as number;
  public focusedElementId: string | null = null;

  public privateIntakesNaturalPersonsFg = {} as FormGroup;
  public privateDepositesNaturalPersonsFg = {} as FormGroup;
  public equityResultAllocationFg = {} as FormGroup;
  public oldAgeTaxAllowanceAdditionFg = {} as FormGroup;
  public oldAgeTaxAllowanceWithdrawalFg = {} as FormGroup;
  public equityMovementOtherFg = {} as FormGroup;
  public equityMovementFg = {} as FormGroup;
  public equityFg = {} as FormGroup;

  public priveOpnamenStortingenSpecificatieFg = {} as FormGroup;

  public equityNaturalPersonDisclosureFg = {} as FormGroup;

  public showDialog(input: number) {
    this.index = input;
    this.dialogVisible = true;
  }

  constructor(
    public algDataService: BokkzAlgDataService,
    public jrDataService: BokkzJrDataService,
    private cdRef: ChangeDetectorRef,
    public jrLogic: BokkzJrLogic,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.createFormGroups();
  }

  get priveOpnamenStortingen(): FormArray {
    return this.priveOpnamenStortingenSpecificatieFg.get('priveOpnamenStortingen') as FormArray;
  }

  addRijPriveOpnamenStortingen(): void {
    this.priveOpnamenStortingen.push(this.createFormGroupPriveOpnameStorting());
  }

  verwijderRijPriveOpnamenStortingen(index: number): void {
    this.priveOpnamenStortingen.removeAt(index, { emitEvent: false });
    this.saveForm();
  }

  get displayNameZakelijkPartner(): string {

    const zakelijkePartner = this.jrEntity.rapportage?.zakelijkePartners?.[this.indexNatuurlijkPersoon];

    if (!zakelijkePartner) {
      return `Zakelijke partner ${this.indexNatuurlijkPersoon + 1}`;
    }

    return this.jrDataService.getDisplayNameZakelijkPartner(zakelijkePartner, this.indexNatuurlijkPersoon)
  }

  get isEquityResultAllocationTrialBalanceItemsButtonVisible() {
    const items = this.equityResultAllocationFg.get('trialBalanceItems');
    return items instanceof FormArray && items.length > 0;
  }

  ngOnInit(): void {
    if (this.selectedTabObservable) {
      this.selectedTabObservable.pipe(takeUntil(this.destroy$)).subscribe((tab) => {
        this.selectedTabSubject.next(tab);
      });
    }

    const index$ = this.route.paramMap.pipe(
      map((params) => Number(params.get('index'))),
      distinctUntilChanged(),
    );

    const jrEntity$ = this.jrDataService.jaarrapportageEntity.pipe(filter((entity) => !!entity));

    combineLatest([index$, jrEntity$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([index, jrEntity]) => {
        this.indexNatuurlijkPersoon = index;
        this.jrEntity = jrEntity;

        this.resetFormGroups();
        this.patchFormGroups();
      });

    this.selectedTabSubject.pipe(distinctUntilChanged()).subscribe((tab) => {
      this.selectedTab = tab;
    });

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this.destroy$)).subscribe((event) => this.handleUrlChange(event.urlAfterRedirects));

    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this.destroy$)).subscribe((event) => this.handleUrlChange(event.urlAfterRedirects));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedTabObservable']) {
      this.selectedTabObservable = changes['selectedTabObservable'].currentValue;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.unsubscribe();
  }

  handleUrlChange(newUrl: string) {
    if (!this.isSaving && this.hasUnsavedChanges()) {
      this.isSaving = true;
      this.jrDataService.updateEntity(this.jrEntity);
      this.isSaving = false;
    }
  }

  private hasUnsavedChanges(): boolean {
    return this.priveOpnamenStortingenSpecificatieFg.dirty || this.equityNaturalPersonDisclosureFg.dirty ||
      this.equityFg.dirty || this.oldAgeTaxAllowanceWithdrawalFg.dirty || this.oldAgeTaxAllowanceAdditionFg.dirty ||
      this.equityMovementOtherFg.dirty;
  }

  public createFormGroups() {
    this.privateIntakesNaturalPersonsFg = this.jrDataService.createFormGroupMonetaryNoDecimalsPeriodValues();
    this.privateDepositesNaturalPersonsFg = this.jrDataService.createFormGroupMonetaryNoDecimalsPeriodValues();
    this.equityResultAllocationFg = this.jrDataService.createFormGroupBalanceItem();
    this.oldAgeTaxAllowanceAdditionFg = this.jrDataService.createFormGroupMonetaryNoDecimalsPeriodValues();
    this.oldAgeTaxAllowanceWithdrawalFg = this.jrDataService.createFormGroupMonetaryNoDecimalsPeriodValues();
    this.equityMovementOtherFg = this.jrDataService.createFormGroupBalanceItem();
    this.equityMovementFg = this.jrDataService.createFormGroupMonetaryNoDecimalsPeriodValues();
    this.equityFg = this.jrDataService.createFormGroupMonetaryNoDecimalsPeriodValues();

    this.priveOpnamenStortingenSpecificatieFg = this.fb.group({
      priveOpnamenStortingen: this.fb.array<EigenVermogenNatuurlijkPersoonPriveOpnameStorting>([]),
    });

    this.equityNaturalPersonDisclosureFg = this.jrDataService.createFormGroupFormattedExplanation();
  }

  private resetFormGroups() {
    this.privateIntakesNaturalPersonsFg.reset();
    this.privateDepositesNaturalPersonsFg.reset();
    this.equityResultAllocationFg.reset();
    this.oldAgeTaxAllowanceAdditionFg.reset();
    this.oldAgeTaxAllowanceWithdrawalFg.reset();
    this.equityMovementOtherFg.reset();
    this.equityMovementFg.reset();
    this.equityFg.reset();
    this.priveOpnamenStortingenSpecificatieFg.reset();
    this.equityNaturalPersonDisclosureFg.reset();
  }

  private patchFormGroups() {
    const balans = this.jrEntity?.rapportage?.jaarrekeningen?.at(0)?.balans;
    if (balans) {
      const eigenVermogenNatuurlijkePersonen = balans.eigenVermogenNatuurlijkePersonen;
      if (eigenVermogenNatuurlijkePersonen) {
        const eigenVermogenNatuurlijkPersoon = eigenVermogenNatuurlijkePersonen.eigenVermogenNatuurlijkePersonenSpecificatie?.at(
          this.indexNatuurlijkPersoon,
        );
        if (eigenVermogenNatuurlijkPersoon) {
          const mutatieoverzicht = eigenVermogenNatuurlijkPersoon.eigenVermogenNatuurlijkPersoonMutatieoverzicht;
          if (mutatieoverzicht) {
            this.privateIntakesNaturalPersonsFg.reset();
            this.privateDepositesNaturalPersonsFg.reset();
            this.equityResultAllocationFg.reset();
            this.equityMovementFg.reset();

            const privateIntakesNaturalPersons = mutatieoverzicht.privateIntakesNaturalPersons;
            if (privateIntakesNaturalPersons) {
              this.privateIntakesNaturalPersonsFg.patchValue(privateIntakesNaturalPersons, {emitEvent: false});
            }
            const privateDepositesNaturalPersons = mutatieoverzicht.privateDepositesNaturalPersons;
            if (privateDepositesNaturalPersons) {
              this.privateDepositesNaturalPersonsFg.patchValue(privateDepositesNaturalPersons, {emitEvent: false});
            }
            const equityResultAllocation = mutatieoverzicht.equityResultAllocation;
            if (equityResultAllocation) {
              this.equityResultAllocationFg.patchValue(equityResultAllocation, {emitEvent: false});
              this.updateTrialBalanceItems(equityResultAllocation?.trialBalanceItems!, this.equityResultAllocationFg.get('trialBalanceItems') as FormArray)
            }
            const oldAgeTaxAllowanceAddition = mutatieoverzicht.oldAgeTaxAllowanceAddition;
            if (oldAgeTaxAllowanceAddition) {
              this.oldAgeTaxAllowanceAdditionFg.patchValue(oldAgeTaxAllowanceAddition);
            }
            const oldAgeTaxAllowanceWithdrawal = mutatieoverzicht.oldAgeTaxAllowanceWithdrawal;
            if (oldAgeTaxAllowanceWithdrawal) {
              this.oldAgeTaxAllowanceWithdrawalFg.patchValue(oldAgeTaxAllowanceWithdrawal);
            }
            const equityMovementOther = mutatieoverzicht.equityMovementOther;
            if (equityMovementOther) {
              this.equityMovementOtherFg.patchValue(equityMovementOther);
              this.updateTrialBalanceItems(
                equityMovementOther?.trialBalanceItems!,
                this.equityMovementOtherFg.get('trialBalanceItems') as FormArray,
              );
            }
            const equityMovement = mutatieoverzicht.equityMovement;
            if (equityMovement) {
              this.equityMovementFg.patchValue(equityMovement, {emitEvent: false});
            }
            const equity = mutatieoverzicht.equity;
            if (equity) {
              this.equityFg.patchValue(equity);
            }
          }

          const tekstueel = eigenVermogenNatuurlijkPersoon.eigenVermogenNatuurlijkPersoonTekstueel;
          if (tekstueel) {
            if (tekstueel.equityNaturalPersonDisclosure) {
              this.equityNaturalPersonDisclosureFg.patchValue(tekstueel.equityNaturalPersonDisclosure);
            }
          }

          this.updateFormPriveOpnamenStortingenSpecificatie(eigenVermogenNatuurlijkPersoon);
        }
      }
    }
  }

  createFormGroupPriveOpnameStorting(item?: EigenVermogenNatuurlijkPersoonPriveOpnameStorting): FormGroup {
    return this.fb.group({
      privateDepositesIntakesDescriptionTypedMember: this.fb.group({
        value: item?.privateDepositesIntakesDescriptionTypedMember?.value || (null as unknown as string),
      }),
      privateIntakesNaturalPersons: this.fb.group({
        valuePeriod1: item?.privateIntakesNaturalPersons?.valuePeriod1 || (null as unknown as number),
        valuePeriod2: item?.privateIntakesNaturalPersons?.valuePeriod2 || (null as unknown as number),
      }),
      privateDepositesNaturalPersons: this.fb.group({
        valuePeriod1: item?.privateDepositesNaturalPersons?.valuePeriod1 || (null as unknown as number),
        valuePeriod2: item?.privateDepositesNaturalPersons?.valuePeriod2 || (null as unknown as number),
      }),
    });
  }

  private updateFormPriveOpnamenStortingenSpecificatie(eigenVermogenNatuurlijkPersoon?: EigenVermogenNatuurlijkPersoon): void {
    const lijst = eigenVermogenNatuurlijkPersoon?.eigenVermogenNatuurlijkPersoonPriveOpnamenStortingenSpecificatie || [];
    const formArray = this.priveOpnamenStortingenSpecificatieFg.get('priveOpnamenStortingen') as FormArray;

    this.syncFormArray(formArray, lijst, (item) => this.createFormGroupPriveOpnameStorting(item), this.focusedElementId);
  }

  updateTrialBalanceItems(lijst?: TrialBalanceItem[], formArray?: FormArray) {
    if (lijst && formArray) {
      for (let i = 0; i < lijst.length; i++) {
        if (i < formArray.length) {
          const control = formArray.at(i) as FormGroup;
          control.patchValue(lijst[i], {emitEvent: false});
        } else {
          const formGroup = this.jrDataService.createFormGroupTrialBalanceItem(lijst[i]);
          formArray.push(formGroup, {emitEvent: false});
        }
      }
    }
  }

  public saveForm(): void {
    this.privateIntakesNaturalPersonsFg.updateValueAndValidity({ emitEvent: false });
    this.privateDepositesNaturalPersonsFg.updateValueAndValidity({ emitEvent: false });
    this.equityResultAllocationFg.updateValueAndValidity({ emitEvent: false });
    this.oldAgeTaxAllowanceWithdrawalFg.updateValueAndValidity({ emitEvent: false });
    this.oldAgeTaxAllowanceAdditionFg.updateValueAndValidity({ emitEvent: false });
    this.equityMovementOtherFg.updateValueAndValidity({ emitEvent: false });
    this.equityMovementFg.updateValueAndValidity({ emitEvent: false });
    this.equityFg.updateValueAndValidity({ emitEvent: false });
    this.priveOpnamenStortingenSpecificatieFg.updateValueAndValidity({ emitEvent: false });
    this.equityNaturalPersonDisclosureFg.updateValueAndValidity({ emitEvent: false });

    let eigenVermogenNatuurlijkePersonenSpecificatie =
      this.jrEntity?.rapportage?.jaarrekeningen?.at(0)?.balans?.eigenVermogenNatuurlijkePersonen
        ?.eigenVermogenNatuurlijkePersonenSpecificatie;
    if (eigenVermogenNatuurlijkePersonenSpecificatie) {
      eigenVermogenNatuurlijkePersonenSpecificatie[this.indexNatuurlijkPersoon] = {
        ...eigenVermogenNatuurlijkePersonenSpecificatie[this.indexNatuurlijkPersoon],
        eigenVermogenNatuurlijkPersoonMutatieoverzicht: {
          ...eigenVermogenNatuurlijkePersonenSpecificatie[this.indexNatuurlijkPersoon]
            ?.eigenVermogenNatuurlijkPersoonMutatieoverzicht,
          oldAgeTaxAllowanceAddition: this.oldAgeTaxAllowanceAdditionFg.value,
          oldAgeTaxAllowanceWithdrawal: this.oldAgeTaxAllowanceWithdrawalFg.value,
          equityMovementOther: this.equityMovementOtherFg.value,
          equity: this.equityFg.value,
        },
        eigenVermogenNatuurlijkPersoonPriveOpnamenStortingenSpecificatie:
        this.priveOpnamenStortingenSpecificatieFg.getRawValue().priveOpnamenStortingen,
        eigenVermogenNatuurlijkPersoonTekstueel: {
          ...eigenVermogenNatuurlijkePersonenSpecificatie[this.indexNatuurlijkPersoon]?.eigenVermogenNatuurlijkPersoonTekstueel,
          equityNaturalPersonDisclosure: this.equityNaturalPersonDisclosureFg.value,
        },
      } as EigenVermogenNatuurlijkPersoon;
    }

    this.jrDataService.updateEntity(this.jrEntity);
    this.jrLogic.getCalculation(this.jrEntity);
  }

  //generieke synchronisatie-helper
  private syncFormArray<T extends { [key: string]: any }>(
    formArray: FormArray,
    data: T[],
    createGroup: (item: T) => FormGroup,
    focusedElementId?: string | null,
  ): void {
    if (formArray) {
      while (formArray.length > data.length) {
        formArray.removeAt(formArray.length - 1, {emitEvent: false});
      }

      //voeg ontbrekende controls toe
      while (formArray.length < data.length) {
        const index = formArray.length;
        formArray.push(createGroup(data[index]));
      }

      //patch controls
      for (let i = 0; i < data.length; i++) {
        const control = formArray.at(i) as FormGroup;

        if (control) {
          control.patchValue(data[i], {emitEvent: false});
        }
      }
    }
  }

  protected readonly FormGroup = FormGroup;
}
